import {
  DirectionalHint,
  Stack,
  Text,
  TooltipHost,
  makeStyles,
} from '@fluentui/react';
import clsx from 'clsx';
import {
  FormHookAmount,
  FormHookDropdown,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import { useCommonStyles } from 'common/styles';
import React from 'react';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import { useDropdownOptions } from '../util';

const TRANSACTION_VALUES_TOOLTIP =
  'Enter test values to simulate how the approval flow responds to a transaction request.';

export const useStyles = makeStyles((theme) => ({
  flexHalf: {
    flex: 0.5,
  },
  container: {
    width: '100%',
  },
}));

export const TestCaseFormContent: React.FC<{
  approvalTypes: ApprovalSetupFlowCommonData | undefined;
  disabled?: boolean;
}> = ({ approvalTypes, disabled = false }) => {
  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const {
    currencies,
    purchaseOrder,
    tags,
    userGroups,
    activeProfiles,
    ranks,
    approvalPhases,
    rowSecurity,
    corporateWorkgroup,
    additionalBusUnit,
    approvalLevelValues,
    departments,
    statusTypes,
  } = useDropdownOptions(approvalTypes);

  return (
    <Stack tokens={{ childrenGap: 50, padding: '0px 0px 100px 0px' }}>
      <Stack tokens={{ childrenGap: 20, padding: '0px 20px' }}>
        <FormHookTextField
          label="Description"
          name="comment"
          disabled={disabled}
        />
      </Stack>

      <Stack tokens={{ childrenGap: 20, padding: '0px 20px' }}>
        <TooltipHost
          content={TRANSACTION_VALUES_TOOLTIP}
          directionalHint={DirectionalHint.topCenter}
          styles={{ root: { width: 350 } }}
        >
          <Text
            variant="xLarge"
            className={clsx(commonStyles.colorThemePrimary)}
          >
            Transaction Values:
          </Text>
        </TooltipHost>
        <Text
          variant="large"
          className={clsx(commonStyles.colorThemePrimary, commonStyles.bold)}
        >
          Primary
        </Text>

        <Stack tokens={{ childrenGap: 20 }}>
          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                options={additionalBusUnit}
                label="Business unit"
                name="businessUnitId"
                disabled={disabled}
              />
            </Stack.Item>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                options={departments}
                label="Department"
                name="departmentId"
                disabled={disabled}
              />
            </Stack.Item>
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={styles.flexHalf}>
              <FormHookAmount
                name={`totalAmount`}
                label="Amount value"
                fixedDecimalScale
                decimalScale={2}
                allowNegative={false}
                disabled={disabled}
              />
            </Stack.Item>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                label="Currency"
                options={currencies}
                name="currencyId"
                disabled={disabled}
              />
            </Stack.Item>
          </Stack>
        </Stack>
      </Stack>

      <Stack tokens={{ childrenGap: 20, padding: '0px 20px' }}>
        <Text
          variant="large"
          className={clsx(commonStyles.colorThemePrimary, commonStyles.bold)}
        >
          Secondary
        </Text>

        <Stack tokens={{ childrenGap: 20 }}>
          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                options={approvalPhases}
                label="Project phase"
                name="companyPhaseTypeId"
                disabled={disabled}
              />
            </Stack.Item>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                options={activeProfiles}
                label="Requestor"
                name="requesterUserProfileId"
                disabled={disabled}
              />
            </Stack.Item>
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                label="User group"
                options={userGroups}
                name="userGroupId"
                disabled={disabled}
              />
            </Stack.Item>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                label="Work group"
                options={corporateWorkgroup}
                name="workgroupId"
                disabled={disabled}
              />
            </Stack.Item>
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                label="Hint"
                options={statusTypes}
                name="transactionHintId"
                disabled={disabled}
              />
            </Stack.Item>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                label="PO Type"
                options={purchaseOrder}
                name="purchaseOrderTypeId"
                disabled={disabled}
              />
            </Stack.Item>
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                label="Tag"
                options={tags}
                name="tagId"
                disabled={disabled}
              />
            </Stack.Item>
            <Stack.Item className={styles.flexHalf}>
              <FormHookAmount
                name="elapsedDays"
                label="Days Elapsed"
                fixedDecimalScale
                decimalScale={0}
                allowNegative={false}
                disabled={disabled}
              />
            </Stack.Item>
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                label="Flow selector"
                options={approvalLevelValues}
                name="approvalLevelValueId"
                disabled={disabled}
              />
            </Stack.Item>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                label="Rank"
                options={ranks}
                name="userRankId"
                disabled={disabled}
              />
            </Stack.Item>
          </Stack>

          <FormHookDropdown
            label="Row Security"
            options={rowSecurity}
            name="rowSecurityId"
            disabled={disabled}
          />
        </Stack>
      </Stack>

      <Stack tokens={{ childrenGap: 20, padding: '0px 20px' }}>
        <Text
          variant="large"
          className={clsx(commonStyles.colorThemePrimary, commonStyles.bold)}
        >
          Additional Bus. Unit/ Departments
        </Text>
        <Stack tokens={{ childrenGap: 20 }}>
          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                options={additionalBusUnit}
                label="Business unit"
                name="additionalBusinessUnitId"
                disabled={disabled}
              />
            </Stack.Item>
            <Stack.Item className={styles.flexHalf}>
              <FormHookDropdown
                options={departments}
                label="Department"
                name="additionalDepartmentId"
                disabled={disabled}
              />
            </Stack.Item>
          </Stack>
          <Stack style={{ width: `calc(50% - 10px)` }}>
            <FormHookAmount
              name={`additionalAmount`}
              label="Amount value"
              fixedDecimalScale
              decimalScale={2}
              allowNegative={false}
              disabled={disabled}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
